import React from 'react';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import { ServiceLocationHierarchy } from '../../../../../@types/service.type';

// ── Hierarchy helpers ────────────────────────────────────────

/** Walk the hierarchy tree to find a node by nodeTypeCode. */
export const findNodeByType = (
  node: ServiceLocationHierarchy | null,
  typeCode: string,
): ServiceLocationHierarchy | null => {
  if (!node) return null;
  if (node.nodeTypeCode === typeCode) return node;
  for (const child of node.children ?? []) {
    const found = findNodeByType(child, typeCode);
    if (found) return found;
  }
  return null;
};

/** Walk to find the node that matches a given id. */
export const findNodeById = (
  node: ServiceLocationHierarchy | null,
  id: number,
): ServiceLocationHierarchy | null => {
  if (!node) return null;
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
};

/** Sum serviceCount and partCount for a node and all its descendants. */
const getTotalCounts = (
  node: ServiceLocationHierarchy,
): { services: number; parts: number } => {
  let services = node.serviceCount ?? 0;
  let parts = node.partCount ?? 0;
  for (const child of node.children ?? []) {
    const childTotals = getTotalCounts(child);
    services += childTotals.services;
    parts += childTotals.parts;
  }
  return { services, parts };
};

// ── Hierarchy node component ─────────────────────────────────

/** Collapsible hierarchy node with linked counts. */
export const HierarchyNode: React.FC<{
  node: ServiceLocationHierarchy;
  depth?: number;
  activeNodeId?: number;
}> = ({ node, depth = 0, activeNodeId }) => {
  const hasChildren = node.children?.length > 0;
  const isActive = activeNodeId != null && node.id === activeNodeId;
  const totals = getTotalCounts(node);

  return (
    <Box sx={{ pl: depth > 0 ? 1.5 : 0 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25,
          bgcolor: isActive ? 'v4.tableSelected' : 'transparent',
          borderRadius: 0.5,
          px: 0.5,
          mx: -0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: isActive ? 700 : depth === 0 ? 600 : 400,
          }}
        >
          {node.name}
        </Typography>
        {totals.services > 0 && (
          <MuiLink
            component={RouterLink}
            to={`/services?locationId=${node.id}`}
            underline="none"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              fontSize: '10px', fontWeight: 600, ml: 0.5,
              bgcolor: 'v4.accent', color: 'common.white',
              px: 0.75, py: '1px', borderRadius: '10px',
              lineHeight: '16px', '&:hover': { opacity: 0.85 },
            }}
          >
            {totals.services} {totals.services === 1 ? 'job' : 'jobs'}
          </MuiLink>
        )}
        {totals.parts > 0 && (
          <MuiLink
            component={RouterLink}
            to={`/parts?locationId=${node.id}`}
            underline="none"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{
              fontSize: '10px', fontWeight: 600, ml: 0.5,
              bgcolor: 'v4.tableSelected', color: 'text.primary',
              px: 0.75, py: '1px', borderRadius: '10px',
              lineHeight: '16px', '&:hover': { opacity: 0.85 },
            }}
          >
            {totals.parts} {totals.parts === 1 ? 'part' : 'parts'}
          </MuiLink>
        )}
      </Box>
      {hasChildren &&
        node.children.map((child) => (
          <HierarchyNode
            key={child.id}
            node={child}
            depth={depth + 1}
            activeNodeId={activeNodeId}
          />
        ))}
    </Box>
  );
};
